import classnames from 'classnames';
import React, { FunctionComponent, HTMLAttributes } from 'react';

import { Attachment, isVideoType, PreviewType } from '@api/v4/attachments/attachmentTypes';
import { WEB_FRIENDLY_ATTACHMENT_TYPES } from '@helpers/attachment_conversion';
import { FontIcon, FontIcons } from '@components/library/icon';
import { getIsFilestackUrl } from '@helpers/filestack';

import classes from './styles/open-in-new-tab.module.scss';

interface GetShowOpenInNewTabOptions {
  isDownloadable: boolean;
  isPreviewRenderError: boolean;
  threeSixtyViewer: boolean;
  selectedAttachment?: Attachment;
}

export const getShowOpenInNewTab = (options: GetShowOpenInNewTabOptions): boolean => {
  const { isDownloadable, isPreviewRenderError, selectedAttachment, threeSixtyViewer } = options;

  const isProcessing = selectedAttachment?.is_processing;
  const previewType = selectedAttachment?.preview_type;
  const selectedExtension = selectedAttachment?.extension?.toLowerCase() || '';

  const showOpenInNewTab = selectedAttachment
  && (WEB_FRIENDLY_ATTACHMENT_TYPES.includes(selectedExtension) || previewType === PreviewType.PDF || previewType === PreviewType.OfficeDoc)
  && !!isDownloadable
  && !isPreviewRenderError
  && !threeSixtyViewer
  && !BFG.brandfolderSettings?.hide_asset_open_in_new_tab
  && !isVideoType(previewType)
  && !isProcessing
  && (previewType !== PreviewType.Audio);

  return showOpenInNewTab || false;
};

interface GetEnableOpenInNewTabOptions {
  showOpenInNewTab: boolean;
  selectedAttachment?: Attachment;
}

export const getEnableOpenInNewTab = (options: GetEnableOpenInNewTabOptions): boolean => {
  const { showOpenInNewTab, selectedAttachment } = options;

  const previewType = selectedAttachment?.preview_type;

  return showOpenInNewTab && previewType !== PreviewType.PDF;
};

type HTMLAnchorElementOmittedProps = Omit<HTMLAttributes<HTMLAnchorElement>, 'href' | 'rel' | 'target'>;

interface OpenInNewTabProps extends HTMLAnchorElementOmittedProps {
  isDownloadable: boolean;
  isPreviewRenderError: boolean;
  threeSixtyViewer: boolean;
  selectedAttachment?: Attachment;
  selectedAttachmentKey?: string;
}

export const OpenInNewTab: FunctionComponent<OpenInNewTabProps> = (props) => {
  const {
    children,
    isDownloadable,
    isPreviewRenderError,
    selectedAttachment,
    selectedAttachmentKey,
    threeSixtyViewer,
    ...otherProps
  } = props;

  const getOpenInNewTabHref = (attachment: Attachment, attachmentKey: string): string | undefined => {
    const previewType = attachment?.preview_type;
    const extension = attachment?.extension?.toLowerCase() || '';

    if (previewType === PreviewType.OfficeDoc || (previewType === PreviewType.Image && getIsFilestackUrl(attachment.url))) {
      return `/viewer/${BFG.brandfolder_slug}/${attachmentKey}`;
    }
    if (previewType === PreviewType.Image || previewType === PreviewType.PDF || WEB_FRIENDLY_ATTACHMENT_TYPES.includes(extension)) {
      return attachment.url;
    }
    return undefined;
  };

  const showOpenInNewTab = getShowOpenInNewTab({
    isDownloadable,
    isPreviewRenderError,
    selectedAttachment,
    threeSixtyViewer
  });

  const href = getOpenInNewTabHref(selectedAttachment, selectedAttachmentKey);

  if (!showOpenInNewTab || !href) return null;

  return (
    <a
      {...otherProps}
      className={classnames(
        classes['open-in-new-tab'],
        otherProps?.className
      )}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children || (
        <>
          <FontIcon aria-hidden icon={FontIcons.NewTab} iconSize={12} />{' '}
          {bfTranslate('Open in new tab')}
        </>
      )}
    </a>
  );
};
